import type { NuxtApp } from '#app';
// import { ApiError } from '@rendora/common/entities';
import { COOKIE_KEY } from '~/constants/config';
import { isChinese } from '~/utils/area';
import { LogType } from '~/utils/logger';
import type { IResponse, RequestConfig } from './interface/common';
import { logoutForBe } from './module/auth';

export const throwErrorCode = 100001;

export enum ServerErrorCode {
  USER_EXIST_ERROR = 11004,
  EMAIL_NOT_REGISTERED = 11016,
}

export async function fetch<K extends IResponse = IResponse>(
  url: string,
  config?: RequestConfig,
  nuxtApp?: NuxtApp,
): Promise<IResponse<K['data'] | undefined>> {
  const params = config?.params || {};

  // 暂时这么处理，对于patch以及其他请求可能有漏洞。最好方案是nginx设置响应头
  if (config?.method?.toLowerCase() === 'get' && !config.noTimestamp) {
    Object.assign(params, { _t: new Date().getTime() });
  }

  const mergedConfig: RequestConfig = {
    autoErrorHandle: true,
    credential: true,
    ...config,
    ...{ params },
  };

  let session: any = {};
  let app = nuxtApp;
  const runtimeConfig = nuxtApp?.$config || useRuntimeConfig();
  // 当接口不需要走任何鉴权，且执行在服务端运行时
  if (mergedConfig.credential) {
    app = nuxtApp || tryUseNuxtApp() || undefined;
    session = import.meta.client
      ? localGetItem(COOKIE_KEY.USER_TOKEN)
      : getCookie(COOKIE_KEY.USER_TOKEN, app);
  }

  return await $fetch(url, {
    baseURL: runtimeConfig.public.apiBase,
    timeout: 30 * 1000,

    onRequest({ options, error, response }) {
      options.headers = new Headers({
        ...mergedConfig.headers,
        'Accept-Language': isChinese() ? 'zh-CN' : 'en',
      });

      if (!runtimeConfig.public.privatization && mergedConfig.credential) {
        options.headers.set('Session-User', getCookie(COOKIE_KEY.USER_RANDOM_ID, app)); // 后端埋点数据需要随机id
      }
      // 防一手，后端可能某些环境的接口鉴权一致
      if (config?.credential === false) {
        return;
      }

      if (!session?.user_id) {
        return;
      }

      if (!options.headers.get('Authorization')) {
        options.headers.set('Authorization', `token ${session.user_id} ${session.key}`);
      }
    },
    onRequestError({ request, options, error }) {
      console.warn('[Fetch Error]', request, error);
    },
    async onResponseError({ error, response }) {
      if (response?.status === 401) {
        if (import.meta.client) {
          const { message } = await import('@rendora/common/composables/message');
          message.clear();
          const permissionData = usePermissionData();
          await useLoginOut();
          const link = permissionData.value?.config?.logout_url
            ? permissionData.value.config.logout_url
            : `${location.origin}/login?redirect=${encodeURIComponent(encodeURIComponent(location.href))}`;
          location.href = link!;
          return;
        } else if (app?.payload.state) {
          deleteCookie(COOKIE_KEY.USER_TOKEN, app);
          const userInfo = toRef(app.payload.state, '$suserInfo');
          const accountList = toRef(app.payload.state, '$saccountList');
          const session = toRef(app.payload.state, `$s${COOKIE_KEY.USER_TOKEN}`);
          await concurrency([
            logoutForBe(),
            fetch('/api/session', { method: 'DELETE', baseURL: '' }),
          ]);
          userInfo.value = undefined;
          accountList.value = [];
          session.value = undefined;
          // 登录态失效，则禁用缓存，服务端不处理重定向（因为没有登录态拿不到重定向地址）
          const ssrContext = app.ssrContext as any;
          if (typeof ssrContext === 'object' && ssrContext) {
            ssrContext.noStore = true;
          }
        }
      }

      if (mergedConfig.autoErrorHandle && import.meta.client) {
        const { message } = await import('@rendora/common/composables/message');
        message.error(error?.message || response._data.msg || '系统异常');
      }
    },

    async onResponse({ response }) {
      if (response.status === 200 && response._data.code !== 0) {
        if (import.meta.client && mergedConfig.autoErrorHandle) {
          const { message } = await import('@rendora/common/composables/message');
          message.error(response._data.msg);
        }
        // throw new ApiError(response._data.msg, response.status, response as any, response._data);
      }
    },
    parseResponse(responseText) {
      try {
        const data = JSON.parse(responseText);

        const assemblyData =
          'error_code' in data
            ? { ...data, code: data.error_code, msg: data.error_reason }
            : { data, code: 0, msg: '' }; // 消息中心没有data层，因此这里兼容

        return assemblyData;
      } catch (error) {
        console.error(`[${LogType.API_ERROR}]`, error);
        return {
          data: undefined,
          code: throwErrorCode,
          msg: 'system error',
        };
      }
    },
    ...mergedConfig,
  });
}
